import React, { Suspense, lazy } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import MainLayout from './components/layouts/MainLayout';
// Dashboard is the landing page -> keep eager for fast first paint. Every other page
// is route-level code-split (React.lazy) so the heavy editors (PipelineBuilder/reactflow,
// TransformationWorkbench, SqlTrinoPage/code-editor, DashboardBuilder/echarts) load only
// when visited, shrinking the initial main bundle.
import Dashboard from './pages/Dashboard';
import { getAddonRoutePath, renderAddonRoute } from './addons/compiledAddonRegistry';
import { useAuth } from './hooks/useAuth';
import { useFeatures } from './hooks/useFeatures';
import { useAddons } from './hooks/useAddons';
import RequireEditor from './components/RequireEditor';
import SessionExpiredDialog from './components/SessionExpiredDialog';
import SignInFailed from './components/auth/SignInFailed';
import { canAdminActions, canAdminRules, canAdminChannels, canUseTransformations } from './utils/capabilities';

// GISMapping carries maplibre-gl (the heaviest page) — split into its own chunk.
const GISMapping = lazy(() => import('./pages/GISMapping'));
// Zones (geofence authoring) also carries maplibre-gl — keep it in its own chunk.
const Zones = lazy(() => import('./pages/Zones'));
const DataSources = lazy(() => import('./pages/DataSources'));
const Pipelines = lazy(() => import('./pages/Pipelines'));
const PipelineBuilder = lazy(() => import('./pages/PipelineBuilder'));
const EntityModels = lazy(() => import('./pages/EntityModels'));
const LineageView = lazy(() => import('./pages/LineageView'));
const DashboardCreator = lazy(() => import('./pages/DashboardCreator'));
const Reports = lazy(() => import('./pages/Reports'));
const SqlTrinoPage = lazy(() => import('./pages/SqlTrinoPage'));
const TransformationWorkbench = lazy(() => import('./pages/TransformationWorkbench'));
const Support = lazy(() => import('./pages/Support'));
const Settings = lazy(() => import('./pages/Settings'));
const Groups = lazy(() => import('./pages/Groups'));
const NotFound = lazy(() => import('./pages/NotFound'));
const DashboardViewer = lazy(() => import('./pages/DashboardViewer'));
const DashboardBuilder = lazy(() => import('./pages/DashboardBuilder'));
const ActionsInbox = lazy(() => import('./pages/ActionsInbox'));
const ActionDetailPage = lazy(() => import('./pages/ActionDetailPage'));
const ActionGroups = lazy(() => import('./pages/ActionGroups'));
const RulesAdmin = lazy(() => import('./pages/RulesAdmin'));
const Channels = lazy(() => import('./pages/Channels'));

const App: React.FC = () => {
  const { loading, phase, user } = useAuth();
  const { loading: featuresLoading, actionsEnabled, rulesEnabled, channelsEnabled, dashboardsEnabled, gisEnabled, entityModelsUiEnabled, trinoEnabled } = useFeatures();
  const { loading: addonsLoading, addonRouteExtensions } = useAddons();
  const compiledAddonRoutes = addonRouteExtensions
    .map(extension => {
      const path = getAddonRoutePath(extension);
      const element = renderAddonRoute(extension);
      if (!path || !element) {
        return null;
      }
      return {
        key: `${extension.addonId}:${extension.extensionKey}`,
        path: path.replace(/^\/+/, ''),
        element,
      };
    })
    .filter((route): route is { key: string; path: string; element: React.ReactElement } => Boolean(route));

  // A failed sign-in is shown before the feature and addon gates, which would otherwise wait on
  // requests that cannot succeed without a token.
  if (phase === 'failed') {
    return <SignInFailed productName="Stratforth" />;
  }

  // Show loading indicator while checking authentication status
  if (loading || featuresLoading || addonsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // With onLoad: 'login-required', Keycloak handles authentication automatically
  // No need for manual authentication checks or login page.
  // Suspense boundary for route-level code-splitting. MainLayout also wraps its Outlet
  // in its own Suspense so the nav shell stays mounted while a page chunk loads; this
  // outer boundary covers lazy routes rendered outside the layout (e.g. 404).
  return (
    <>
    {/* Global session-expiry prompt (re-login affordance instead of silent logout). */}
    <SessionExpiredDialog />
    <Suspense
      fallback={
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      }
    >
    <Routes>
      {/* All routes are now protected by Keycloak */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="actions" element={actionsEnabled ? <ActionsInbox /> : <Navigate to="/" replace />} />
        <Route path="actions/:actionId" element={actionsEnabled ? <ActionDetailPage /> : <Navigate to="/" replace />} />
        <Route path="action-groups" element={actionsEnabled && canAdminActions(user?.roles) ? <ActionGroups /> : <Navigate to="/" replace />} />
        {/* Rules admin is operational-only (ADR-022 §12) and admin-gated; authoring lives in the builder. */}
        <Route path="rules" element={rulesEnabled && canAdminRules(user?.roles) ? <RulesAdmin /> : <Navigate to="/" replace />} />
        {/* Outbound channels config (ADR-023 §3), tenant-admin gated. Customer surface only. */}
        <Route path="channels" element={channelsEnabled && canAdminChannels(user?.roles) ? <Channels /> : <Navigate to="/" replace />} />
        {/* Edit-oriented routes: guarded so a positively-identified viewer-only user
            deep-linking here gets a read-only notice instead of an editor page that 403s.
            RequireEditor is DEFAULT-OPEN (dev/no-auth and unknown roles keep full access). */}
        <Route element={<RequireEditor />}>
          <Route path="data-sources" element={<DataSources />} />
          <Route path="pipelines" element={<Pipelines />} />
          <Route path="pipeline-builder/:pipelineId" element={<PipelineBuilder />} />
          <Route path="transformations" element={canUseTransformations(user?.roles) ? <TransformationWorkbench /> : <Navigate to="/" replace />} />
        </Route>
        <Route
          path="entity-models"
          element={
            entityModelsUiEnabled ? (
              <RequireEditor>
                <EntityModels />
              </RequireEditor>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="lineage" element={<LineageView />} />
        <Route path="visualiser" element={<Navigate to="/dashboards" replace />} />
        {compiledAddonRoutes.map(route => (
          <Route key={route.key} path={route.path} element={route.element} />
        ))}
        <Route path="dashboards" element={dashboardsEnabled ? <DashboardCreator /> : <Navigate to="/" replace />} />
        {/* Reports is a viewer-safe, PDF-ready catalog over the same tenant-scoped,
            share-scoped dashboards; gate it on the dashboards feature like the library and
            do NOT wrap it in RequireEditor so read-only viewers can open/export reports. */}
        <Route path="reports" element={dashboardsEnabled ? <Reports /> : <Navigate to="/" replace />} />
        <Route path="dashboards/:dashboardId" element={dashboardsEnabled ? <DashboardViewer /> : <Navigate to="/" replace />} />
        <Route
          path="dashboards/:dashboardId/edit"
          element={
            dashboardsEnabled ? (
              <RequireEditor>
                <DashboardBuilder />
              </RequireEditor>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="gis" element={gisEnabled ? <GISMapping /> : <Navigate to="/" replace />} />
        {/* Geofence zones are a Phase 3 Rules feature (ADR-022 §14): gate on rulesEnabled,
            keep RequireEditor (authoring is editor-only). Reconciled at Wave-3 integration —
            supersedes the gisEnabled gate the zones workstream shipped standalone. */}
        <Route
          path="zones"
          element={
            rulesEnabled ? (
              <RequireEditor>
                <Zones />
              </RequireEditor>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="sql/trino" element={trinoEnabled ? <SqlTrinoPage /> : <Navigate to="/" replace />} />
        <Route path="groups" element={<Groups />} />
        <Route path="support" element={<Support />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </>
  );
};

export default App;
